import { useAuth } from '@/context/auth';
import { useUserData } from '@/context/auth/user-data-context';
import { Link } from '@/i18n/navigation';
import ProfilePicto from '@public/profile-picto.svg';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type Props = {
    className?: string;
    iconOnly?: boolean;
    margin?: string;
    isScrolled?: boolean;
};

export default function AccountDropDown({
    className,
    iconOnly = false,
    margin = 'ml-auto',
    isScrolled = false,
}: Props) {
    const [currentMenuOpen, setCurrentMenuOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const segments = useSelectedLayoutSegments();

    const listItemClassName: string = `whitespace-nowrap flex rounded-[8px] p-[10px] my-2 relative text-base
                                        hover:bg-[rgba(255,255,255,0.30)] cursor-pointer
                                        after:content-[''] after:absolute
                                        after:-bottom-1 after:left-0
                                        after:w-full after:h-[1px]
                                        after:bg-[rgba(255,255,255,0.10)]`;
    const activeListItemClassName: string = `whitespace-nowrap flex rounded-[8px] p-[10px] my-2 relative text-base
                                        bg-[#DD06AE]
                                        after:content-[''] after:absolute
                                        after:-bottom-1 after:left-0
                                        after:w-full after:h-[1px]
                                        after:bg-[rgba(255,255,255,0.10)]`;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                (event.target instanceof Element && event.target.matches('a')) ||
                (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target))
            ) {
                setCurrentMenuOpen(false);
            }
        };

        if (currentMenuOpen) document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [currentMenuOpen]);

    const { user, signin, signout } = useAuth();
    const { name, isSeller, isBuyer } = useUserData();

    const t = useTranslations('Header');
    const tNav = useTranslations('Navigation');

    const customClassName = `${className} flex justify-between items-center rounded-[7px] text-white px-4 py-3 transition duration-300 cursor-pointer flex-shrink-0`;

    if (user) {
        if (isSeller || isBuyer) {
            return (
                <div ref={containerRef} className="relative">
                    <button
                        type="button"
                        className={`
                            ${className}
                            ${segments[0] === 'settings' || segments[0] === 'transactions' ? 'bg-white/30' : isScrolled ? 'bg-white/10 hover:bg-white/25' : 'bg-white/10 hover:bg-white/15'}
                            px-3 py-3 rounded-[8px] cursor-pointer flex
                            transition-colors
                            ml-4
                        `}
                        onClick={() => setCurrentMenuOpen(!currentMenuOpen)}
                    >
                        <Image src={ProfilePicto.src} alt="profile picto" width={24} height={24} />
                        {!iconOnly ? (
                            <>
                                <span className="truncate whitespace-nowrap overflow-hidden max-w-[150px] ml-2 pr-[10px]">
                                    {name}
                                </span>
                                <ChevronDown
                                    className={`transition-transform duration-200 ${currentMenuOpen ? 'rotate-180' : ''}`}
                                />
                            </>
                        ) : (
                            ''
                        )}
                    </button>
                    <ul
                        className={`
                        px-[10px] pt-1 pb-[0.5px] absolute right-0 bg-[rgba(14,14,14,1)] rounded-[8px] z-1
                        w-auto
                        md:max-w-[300px]
                        lg:min-w-[230px]
                        lg:max-w-[400px]
                        max-h-[440px] sm:max-h-[initial]
                        overflow-y-auto
                        ${currentMenuOpen ? 'block' : 'hidden'}
                    `}
                    >
                        {isBuyer ? (
                            <Link
                                className={segments[0] === 'transactions' ? activeListItemClassName : listItemClassName}
                                href="/transactions"
                            >
                                {tNav('buyer.transactions')}
                            </Link>
                        ) : null}
                        {isBuyer ? (
                            <Link
                                className={`${segments.join('/').includes('licenses') ? activeListItemClassName : listItemClassName} ${isBuyer ? 'lg:hidden' : ''}`}
                                href="/licenses"
                            >
                                {tNav('licenses')}
                            </Link>
                        ) : null}
                        <li>
                            <Link
                                className={segments[0] === 'settings' ? activeListItemClassName : listItemClassName}
                                href="/settings"
                            >
                                {t('accountSettings')}
                            </Link>
                        </li>
                        <li>
                            <button
                                type="button"
                                className={`${listItemClassName} w-full after:content-[none]`}
                                onClick={signout}
                            >
                                {t('Sign out')}
                            </button>
                        </li>
                    </ul>
                </div>
            );
        }

        return (
            <button type="button" onClick={signout} className={iconOnly ? `${className} ${margin}` : customClassName}>
                <Image src={ProfilePicto.src} alt="profile picto" className="mr-2" width={24} height={24} />
                {!iconOnly ? <span>{t('Sign out')}</span> : ''}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={signin}
            className={iconOnly ? `${className} ${margin} flex-shrink-0` : customClassName}
        >
            <Image src={ProfilePicto.src} alt="profile picto" className="mr-2" width={24} height={24} />
            {!iconOnly ? <span>{t('Sign in')}</span> : ''}
        </button>
    );
}
